// frontend/src/modules/projects/components/ReleaseBilledTimeCard.tsx
//
// The user-reachable half of POST /api/projects/unbill-time.
//
// `time_entries.billed_invoice_id` is a one-way stamp everywhere else in the
// codebase, and invoices are cancelled by status flag rather than deleted. So
// invoicing the wrong client or period and then cancelling the invoice used to
// make those hours permanently invisible to the billable list — a state you
// could enter but not leave, recoverable only with direct SQL. The endpoint
// undoes it; this card is what makes the endpoint reachable.
//
// Contract: POST /api/projects/unbill-time { invoice_id, entry_ids? }
//   permission `invoices.write` (the same key that ISSUED the invoice — this
//   is its inverse); 409 `invoice_not_cancelled` while the invoice is still
//   live, because clearing the link then would let the same hours be billed
//   twice. Response { invoice_id, unbilled_count, entry_ids }.
//
// Rendered only on a CANCELLED invoice. There is no endpoint that reports how
// much time a given invoice consumed, so the card does not promise a number
// up front — it reports the server's `unbilled_count` afterwards, including
// the honest "nothing was attached" case.

import * as React from 'react';
import { toast } from 'sonner';
import { Clock4 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { usePermission } from '../../../core/auth/usePermission';
import { useEnabledModules } from '../../../core/modules/EnabledModulesProvider';
import { projectsApi } from '../api';

export function ReleaseBilledTimeCard({
  invoiceId,
  invoiceNumber,
}: {
  invoiceId: string;
  invoiceNumber?: string | null;
}) {
  const confirm = useConfirm();
  const canWrite = usePermission('invoices.write');
  const { modules } = useEnabledModules();
  const [busy, setBusy] = React.useState(false);
  const [released, setReleased] = React.useState<number | null>(null);

  // Tracked time only exists when the projects module is on, and only an
  // invoices.write holder may un-bill (mirrors the route's gate).
  if (!canWrite || !modules.has('projects')) return null;

  async function release() {
    const ok = await confirm({
      title: 'Release the tracked time on this invoice?',
      description: `Any hours billed to ${invoiceNumber ?? 'this invoice'} become billable again and can be put on a new invoice. The cancelled invoice itself is unchanged.`,
      confirmLabel: 'Release time',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await projectsApi.unbillTime(invoiceId);
      setReleased(res.unbilled_count);
      if (res.unbilled_count === 0) {
        toast.warning('No tracked time was attached to this invoice');
      } else {
        toast.success(
          `Released ${res.unbilled_count} time entr${res.unbilled_count === 1 ? 'y' : 'ies'}`,
          { description: 'Those hours are billable again.' },
        );
      }
    } catch (err: any) {
      if (err?.code === 'invoice_not_cancelled') {
        toast.error('Cancel the invoice first', { description: err.message });
      } else {
        toast.error('Could not release the time', { description: err?.message ?? String(err) });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flex items-start gap-3 rounded-2xl px-5 py-4 ring-1 ring-border/60 bg-card/60">
      <Clock4 className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold text-foreground">Tracked time on this invoice</p>
        <p className="text-[12.5px] text-muted-foreground mt-1">
          {released == null
            ? 'Hours billed to a cancelled invoice stay locked to it until they are released. Releasing them makes them billable again — nothing about this invoice changes.'
            : released === 0
              ? 'No tracked time was attached to this invoice, so there was nothing to release.'
              : `${released} time entr${released === 1 ? 'y is' : 'ies are'} billable again.`}
        </p>
      </div>
      {released == null && (
        <Button variant="outline" size="sm" disabled={busy} onClick={release} className="shrink-0">
          {busy ? 'Releasing…' : 'Release billed time'}
        </Button>
      )}
    </section>
  );
}
